import Modal from 'react-modal';
import React, { useCallback, useEffect, useState } from 'react';
import styles from './AddExtraIncomeModal.module.css';
import { ExtraIncome } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import AsyncSelect from 'react-select/async';
import { loadOrderOptions } from '../util/loadOrderOptions';
import { toast } from 'react-toastify';
import updateNumberValue from '../util/updateNumberValue';
import { formatCurrency } from '../util/formatCurrency';

type AddExtraIncomeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  extraIncome?: ExtraIncome | null;
  disableOrderSelect?: boolean;
  selectedOrder?: { label: string; value: number } | null;
};

const AddExtraIncomeModal: React.FC<AddExtraIncomeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  extraIncome,
  disableOrderSelect = false,
  selectedOrder: propSelectedOrder,
}) => {
  const [selectedOrder, setSelectedOrder] = useState<{ label: string; value: number } | null>(null);
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState('0.0');
  const [creditAmount, setCreditAmount] = useState('0.0');
  const [incomeType, setIncomeType] = useState('');
  const [customerCreditBalance, setCustomerCreditBalance] = useState<number | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const resetForm = useCallback(() => {
    setSelectedOrder(null);
    setDate(today);
    setAmount('0.0');
    setCreditAmount('0.0');
    setIncomeType('');
    setErrors({});
    setCustomerCreditBalance(null);
  }, [today]);

  const fetchCustomerCreditBalance = useCallback(async (customerId: string) => {
    try {
      const res = await fetchWithAuth(`/customers/${customerId}`);
      const data = await res.json();
      setCustomerCreditBalance(data.credit_balance ?? null);
    } catch (error) {
      console.error('Error fetching customer credit balance:', error);
    }
  }, []);

  const fetchCreditBalanceForOrder = useCallback(async (orderId: number) => {
    try {
      const res = await fetchWithAuth(`/orders/${orderId}`);
      const data = await res.json();
      if (data.customer_id) {
        fetchCustomerCreditBalance(data.customer_id.toString());
      }
    } catch (error) {
      console.error('Error fetching order for credit balance:', error);
    }
  }, [fetchCustomerCreditBalance]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    } else {
      if (extraIncome) {
        setSelectedOrder({ label: extraIncome.order.order_reference, value: extraIncome.order_id });
        setDate(extraIncome.date);
        setAmount(String(extraIncome.amount));
        setCreditAmount(String(extraIncome.credit_amount ?? 0));
        setIncomeType(extraIncome.income_type);
        fetchCreditBalanceForOrder(extraIncome.order_id);
      } else if (propSelectedOrder) {
        setSelectedOrder(propSelectedOrder);
        fetchCreditBalanceForOrder(propSelectedOrder.value);
      }
    }
  }, [isOpen, extraIncome, propSelectedOrder, fetchCreditBalanceForOrder, resetForm]);

  const handleSave = () => {
    const newErrors: { [key: string]: string } = {};
    if (!selectedOrder?.value) newErrors.orderId = 'Order is required.';
    if (!date) newErrors.date = 'Date is required.';
    if (!amount) newErrors.amount = 'Amount is required.';
    if (!incomeType.trim()) newErrors.incomeType = 'Income type is required.';
    if (customerCreditBalance !== null && Number(creditAmount) > customerCreditBalance) {
      newErrors.creditAmount = `Credit amount cannot exceed customer's credit balance of ${formatCurrency(customerCreditBalance)}.`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const saveExtraIncome = async () => {
      try {
        const url = extraIncome
          ? `/extra_incomes/${extraIncome.id}`
          : '/extra_incomes';
        const method = extraIncome ? 'PATCH' : 'POST';

        const res = await fetchWithAuth(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: selectedOrder?.value || null,
            date,
            amount: Number(amount),
            credit_amount: Number(creditAmount),
            income_type: incomeType,
          }),
        });

        if (res.ok) {
          onSuccess();
          resetForm();
          onClose();
          const message = extraIncome
          ? 'Extra income updated successfully!'
          : 'Extra income added successfully!';
          toast.success(message);
        } else {
          const data = await res.json();
          const msg = data?.errors?.join(', ') || 'Failed to save extra income.';
          toast.error(msg);
        }
      } catch (error) {
        toast.error('An unexpected error occurred while saving extra income.');
      }
    };

    saveExtraIncome();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <h2 className="text-2xl font-bold mb-4">{extraIncome ? 'Edit Extra Income' : 'Add Extra Income'}</h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Order</label>
          <AsyncSelect
            cacheOptions
            loadOptions={loadOrderOptions}
            defaultOptions
            value={selectedOrder}
            onChange={(selectedOption) => {
              setSelectedOrder(selectedOption);
              setCustomerCreditBalance(null);
              if (selectedOption?.value) {
                fetchCreditBalanceForOrder(selectedOption.value);
              }
            }}
            isDisabled={disableOrderSelect}
            placeholder="Select an order..."
          />
          {errors.orderId && <p className="text-red-500 text-sm mt-1">{errors.orderId}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Amount</label>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {updateNumberValue(e.target.value, setAmount)}}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
        </div>

        <div>
          {customerCreditBalance !== null && (
            customerCreditBalance > 0 ? (
              <span className="inline-block bg-green-800 text-green-300 font-bold px-3 py-1 rounded-full text-sm mb-1">
                Credit available
              </span>
            ) : (
              <span className="inline-block bg-gray-300 text-gray-600 font-bold px-3 py-1 rounded-full text-sm mb-1">
                Credit unavailable
              </span>
            )
          )}
          <p className="text-sm text-gray-500 mb-1">
            Customer credit balance is {customerCreditBalance !== null ? formatCurrency(customerCreditBalance) : formatCurrency(0)}
          </p>
          <input
            type="text"
            inputMode="decimal"
            value={creditAmount}
            onChange={(e) => {updateNumberValue(e.target.value, setCreditAmount)}}
            className={`w-full px-3 py-2 border rounded ${(customerCreditBalance ?? 0) <= 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            disabled={(customerCreditBalance ?? 0) <= 0}
          />
          {errors.creditAmount && <p className="text-red-500 text-sm mt-1">{errors.creditAmount}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <select
            value={incomeType}
            onChange={(e) => setIncomeType(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          >
            <option value="">Select a type</option>
            <option value="Extra Weight">Extra Weight</option>
            <option value="Order Expediting">Order Expediting</option>
            <option value="VAT Top Up">VAT Top Up</option>
            <option value="Split Order">Split Order</option>
            <option value="Order Cost Top Up">Order Cost Top Up</option>
          </select>
          {errors.incomeType && <p className="text-red-500 text-sm mt-1">{errors.incomeType}</p>}
        </div>

      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={onClose}
          className="mr-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
        >
          Save
        </button>
      </div>
    </Modal>
  );
};

export default AddExtraIncomeModal;
